// team-status.js — is the team server answering?
//
// In local-daemon mode the daemon forwards /v1/teams/* and /v1/sync/state to
// the team server. When that server stops answering, only the widgets fed by
// it go dark: they say so and the boot fetch retries with backoff. Pages fed
// by the daemon itself (Coordination, Sessions, Settings, Worktrees) keep
// rendering from local data.
import { escapeHTML } from "./util.js";

// Capped low: once the team server answers again, the page should notice
// within seconds, not minutes.
const RETRY_DELAYS_MS = [3000, 6000, 10000, 15000, 20000];

export const teamStatus = {
  unreachable: false,
  // The daemon serving this page did not answer at all (a network error,
  // not an HTTP status), e.g. while it restarts after an upgrade.
  localDown: false,
  error: "",
  attempt: 0,
  retryAt: 0,
};

let retryTimer = null;
let countdownTimer = null;

export function isTeamUnreachable() {
  return teamStatus.unreachable;
}

export function isLocalDaemonDown() {
  return teamStatus.unreachable && teamStatus.localDown;
}

function retryInSeconds() {
  return Math.max(0, Math.ceil((teamStatus.retryAt - Date.now()) / 1000));
}

function retryText() {
  const s = retryInSeconds();
  return s > 0 ? "Retrying in " + s + "s." : "Retrying now…";
}

// markTeamUnreachable records the failure and schedules `retry` (the boot
// fetch) with backoff. A retry already scheduled is kept, so the several
// team calls that fail together count as one attempt.
export function markTeamUnreachable(err, retry, { localDown = false } = {}) {
  teamStatus.unreachable = true;
  // Only the health check says the daemon itself is down; a failed data
  // fetch can't tell a network error from a bug in the page.
  if (localDown) teamStatus.localDown = true;
  teamStatus.error = (err && err.message) || String(err || "");
  if (!retryTimer) {
    const delay = RETRY_DELAYS_MS[Math.min(teamStatus.attempt, RETRY_DELAYS_MS.length - 1)];
    teamStatus.attempt++;
    teamStatus.retryAt = Date.now() + delay;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      retry();
    }, delay);
  }
  if (!countdownTimer) {
    countdownTimer = setInterval(renderTeamStatusBanner, 1000);
  }
  renderTeamStatusBanner();
}

export function markTeamReachable() {
  if (retryTimer) clearTimeout(retryTimer);
  if (countdownTimer) clearInterval(countdownTimer);
  retryTimer = null;
  countdownTimer = null;
  teamStatus.unreachable = false;
  teamStatus.localDown = false;
  teamStatus.error = "";
  teamStatus.attempt = 0;
  teamStatus.retryAt = 0;
  renderTeamStatusBanner();
}

// retryTeamNow skips the remaining backoff (the banner's Retry button).
export function retryTeamNow(retry) {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  teamStatus.retryAt = Date.now();
  renderTeamStatusBanner();
  retry();
}

export function renderTeamStatusBanner() {
  const el = document.getElementById("team-status-banner");
  if (!el) return;
  if (!teamStatus.unreachable) {
    el.style.display = "none";
    return;
  }
  el.style.display = "";
  const text = document.getElementById("team-status-text");
  if (text) {
    text.textContent = teamStatus.localDown
      ? "AgentCollision on this computer isn't answering. It may be restarting after an update. " +
        retryText()
      : "Team server unreachable. " + retryText() +
        " Everything on this machine still works; team-wide leases, files, events and members are paused.";
  }
  if (el.title !== teamStatus.error) el.title = teamStatus.error;
}

// teamUnavailableHTML is the empty state for a widget fed by the team server.
export function teamUnavailableHTML(what) {
  const [title, source] = teamStatus.localDown
    ? ["AgentCollision isn't answering, retrying", "AgentCollision on this computer"]
    : ["Team server unreachable, retrying", "your team server"];
  return `<div class="empty" data-team-unavailable style="opacity:1;animation:none;">
    <div class="empty-title">${title}</div>
    <div class="empty-detail">${escapeHTML(what)} come from ${source}, which is not answering. Retrying automatically; this fills in as soon as it answers.</div>
  </div>`;
}
